"use client";

import React, { useMemo, useState } from "react";
import { InfoCircleOutlined } from "@ant-design/icons";
import { DatePicker, Empty, Select, Skeleton, Tooltip, Typography } from "antd";
import dayjs, { type Dayjs } from "dayjs";

import { CheckoutVariantStatus } from "@fitness-app/data-models/entities/CheckoutVariant";
import { type ProductPrice } from "@fitness-app/data-models/entities/Product";

import { useCheckoutVariants } from "~/modules/Products/Product/ProductDetails/hooks/useCheckoutVariants";
import { ProductCheckoutVariantStatsAggregated } from "./ProductCheckoutVariantStatsAggregated";

interface ProductCheckoutVariantStatsTabProps {
  productId: string;
  prices: ProductPrice[];
}

const ALL_VARIANTS_ITEM_ID = "all";

const { RangePicker } = DatePicker;

const getRangePresets = (): { label: string; value: [Dayjs, Dayjs] }[] => [
  { label: "Ostatnie 7 dni", value: [dayjs().subtract(7, "day"), dayjs()] },
  { label: "Ostatnie 30 dni", value: [dayjs().subtract(30, "day"), dayjs()] },
  { label: "Ostatnie 60 dni", value: [dayjs().subtract(60, "day"), dayjs()] },
  { label: "Ostatnie 90 dni", value: [dayjs().subtract(90, "day"), dayjs()] },
  { label: "Ten miesiąc", value: [dayjs().startOf("month"), dayjs()] },
];

export const ProductCheckoutVariantStatsTab = ({
  productId,
  prices,
}: ProductCheckoutVariantStatsTabProps): React.ReactElement => {
  const { data: variants, isLoading } = useCheckoutVariants(productId);
  const [selectedVariants, setSelectedVariants] = useState<string[]>([ALL_VARIANTS_ITEM_ID]);
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>([dayjs().subtract(30, "day"), dayjs()]);

  const activeVariants = useMemo(() => {
    return (
      variants?.filter(
        (variant) =>
          variant.status === CheckoutVariantStatus.Active || variant.status === CheckoutVariantStatus.Inactive,
      ) ?? []
    );
  }, [variants]);

  const variantOptions = useMemo(() => {
    const options: { label: string; value: string }[] = [{ label: "Wszystkie warianty", value: ALL_VARIANTS_ITEM_ID }];

    activeVariants.forEach((variant) => {
      options.push({
        label: variant.slug || variant.id,
        value: variant.id,
      });
    });

    return options;
  }, [activeVariants]);

  const handleChange = (value: string[]) => {
    if (value.length === 0) {
      setSelectedVariants([ALL_VARIANTS_ITEM_ID]);
      return;
    }

    const newAllSelected = value.includes(ALL_VARIANTS_ITEM_ID) && !selectedVariants.includes(ALL_VARIANTS_ITEM_ID);

    if (newAllSelected) {
      // User selected "Wszystkie warianty", use only that
      setSelectedVariants([ALL_VARIANTS_ITEM_ID]);
    } else if (value.includes(ALL_VARIANTS_ITEM_ID) && value.length > 1) {
      // User selected individual variants while "Wszystkie warianty" was selected
      setSelectedVariants(value.filter((item) => item !== ALL_VARIANTS_ITEM_ID));
    } else {
      setSelectedVariants(value);
    }
  };

  const selectedCheckoutVariantIds = useMemo(() => {
    if (selectedVariants.includes(ALL_VARIANTS_ITEM_ID)) {
      return activeVariants.map((variant) => variant.id);
    }
    return selectedVariants;
  }, [selectedVariants, activeVariants]);

  const selectedVariantPrices = useMemo(() => {
    if (selectedVariants.includes(ALL_VARIANTS_ITEM_ID)) {
      return prices;
    }

    const variantsToUse = activeVariants.filter((variant) => selectedVariants.includes(variant.id));

    const priceIdsSet = new Set<string>();
    variantsToUse.forEach((variant) => {
      variant.prices.forEach((priceId) => priceIdsSet.add(priceId));
    });

    return prices.filter((price) => priceIdsSet.has(price.id));
  }, [selectedVariants, activeVariants, prices]);

  const selectedVariantSlugs = useMemo(() => {
    if (selectedVariants.includes(ALL_VARIANTS_ITEM_ID)) {
      return null;
    }
    return activeVariants.filter((variant) => selectedVariants.includes(variant.id)).map((v) => v.slug || v.id);
  }, [selectedVariants, activeVariants]);

  if (isLoading) {
    return <Skeleton active paragraph={{ rows: 6 }} />;
  }

  if (!activeVariants.length) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="Brak wariantów koszyka. Dodaj warianty w zakładce 'Warianty koszyka'."
        className="py-10"
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-end gap-2 py-4">
        <RangePicker
          value={dateRange}
          presets={getRangePresets()}
          onChange={(value) => setDateRange(value?.[0] && value?.[1] ? [value[0], value[1]] : null)}
          allowClear
          placeholder={["Data od", "Data do"]}
        />
        <div className="flex flex-col items-end gap-2">
          <Select
            mode="multiple"
            value={selectedVariants}
            onChange={handleChange}
            style={{ minWidth: 320, maxWidth: 500 }}
            options={variantOptions}
            placeholder="Wybierz warianty"
            filterOption={(inputValue, option) => {
              if (option?.label) {
                return String(option.label).toUpperCase().indexOf(inputValue.toUpperCase()) !== -1;
              }
              return false;
            }}
          />
          {selectedVariantSlugs && selectedVariantSlugs.length > 0 && (
            <Tooltip
              title={
                <div className="max-h-60 overflow-y-auto">
                  {selectedVariantSlugs.map((slug) => (
                    <div key={slug}>{slug}</div>
                  ))}
                </div>
              }
              placement="bottomRight"
            >
              <Typography.Text type="secondary" className="cursor-pointer text-xs">
                <InfoCircleOutlined className="mr-1" />
                Wybrane warianty: {selectedVariantSlugs.length}
              </Typography.Text>
            </Tooltip>
          )}
        </div>
      </div>

      {selectedCheckoutVariantIds.length > 0 ? (
        <ProductCheckoutVariantStatsAggregated
          checkoutVariantIds={selectedCheckoutVariantIds}
          prices={selectedVariantPrices}
          productId={productId}
          dateRange={dateRange}
        />
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Wybierz warianty do wyświetlenia statystyk" />
      )}
    </div>
  );
};
